/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Admin Users API
 * Admin bounded-context contract for user aggregate and owned task entity.
 *
 * OpenAPI spec version: 1.0.0
 */
import * as zod from "zod";

/**
 * Retrieve a paginated list of users with support for:
 * - Full-text search (name/email)
 * - Role filtering (single or multiple)
 * - Date range filtering (by createdAt)
 * - Department filtering (single or multiple)
 * - Seniority range filtering
 * - Server-side sorting
 * - Pagination
 * @summary List users with optional filtering, sorting, and pagination
 */

export const listUsersQueryPerPageMax = 100;

export const listUsersQuerySortByDefault = `name`;
export const listUsersQuerySortDirDefault = `asc`;

export const ListUsersQueryParams = zod.object({
  page: zod.coerce.number().int().min(1).describe("Page number (1-indexed)"),
  perPage: zod.coerce
    .number()
    .int()
    .min(1)
    .max(listUsersQueryPerPageMax)
    .describe("Number of items per page"),
  q: zod.coerce.string().optional().describe("Free-text search query"),
  sortBy: zod
    .enum([
      "id",
      "name",
      "email",
      "role",
      "isActive",
      "taskCompletionRate",
      "onboardingProgress",
      "createdAt",
    ])
    .default(listUsersQuerySortByDefault)
    .describe("Column to sort by"),
  sortDir: zod
    .enum(["asc", "desc"])
    .default(listUsersQuerySortDirDefault)
    .describe("Sort direction"),
  roles: zod
    .array(zod.enum(["admin", "editor", "viewer"]))
    .optional()
    .describe("Filter by one or more roles"),
  dateRangeStart: zod.iso
    .date()
    .optional()
    .describe("Start date (YYYY-MM-DD) for createdAt filter"),
  dateRangeEnd: zod.iso.date().optional().describe("End date (YYYY-MM-DD) for createdAt filter"),
  departments: zod
    .array(zod.coerce.string())
    .optional()
    .describe("Filter by one or more departments"),
  seniorityRangeMin: zod
    .union([zod.literal(1), zod.literal(2), zod.literal(3), zod.literal(4), zod.literal(5)])
    .optional()
    .describe("Minimum seniority level (1=junior, 5=senior)"),
  seniorityRangeMax: zod
    .union([zod.literal(1), zod.literal(2), zod.literal(3), zod.literal(4), zod.literal(5)])
    .optional()
    .describe("Maximum seniority level (1=junior, 5=senior)"),
});

export const listUsersResponseDataItemTaskCompletionRateMin = 0;
export const listUsersResponseDataItemTaskCompletionRateMax = 100;

export const listUsersResponseDataItemOnboardingProgressMin = 0;
export const listUsersResponseDataItemOnboardingProgressMax = 100;

export const listUsersResponseDataItemLoginCountMin = 0;

export const listUsersResponseMetaTotalMin = 0;

export const ListUsersResponse = zod.object({
  data: zod
    .array(
      zod.object({
        id: zod.string().describe("Unique user identifier"),
        name: zod.string().describe("User's full name"),
        email: zod.email().describe("User's email address"),
        role: zod.enum(["admin", "editor", "viewer"]).describe("User's role"),
        createdAt: zod.iso
          .datetime({offset: true})
          .describe("Account creation date in ISO 8601 format"),
        image: zod.string().describe("Avatar image URL or asset name"),
        isActive: zod.boolean().describe("Whether the user account is active"),
        bio: zod.string().nullish().describe("User biography"),
        taskCompletionRate: zod
          .int()
          .min(listUsersResponseDataItemTaskCompletionRateMin)
          .max(listUsersResponseDataItemTaskCompletionRateMax)
          .describe("Task completion rate (0-100)"),
        onboardingProgress: zod
          .int()
          .min(listUsersResponseDataItemOnboardingProgressMin)
          .max(listUsersResponseDataItemOnboardingProgressMax)
          .describe("Onboarding progress (0-100)"),
        seniority: zod
          .union([zod.literal(1), zod.literal(2), zod.literal(3), zod.literal(4), zod.literal(5)])
          .describe("Seniority level (1=junior, 5=senior)"),
        loginCount: zod
          .int()
          .min(listUsersResponseDataItemLoginCountMin)
          .describe("Total number of successful logins"),
        lastLoginAt: zod.iso
          .datetime({offset: true})
          .nullish()
          .describe("Date of last login in ISO 8601 format"),
        department: zod.string().describe("Department or team name"),
        tasks: zod
          .array(
            zod.object({
              id: zod.string().describe("Unique task identifier"),
              title: zod.string().describe("Task title"),
              status: zod.enum(["pending", "in-progress", "completed"]).describe("Task status"),
              dueAt: zod.iso.datetime({offset: true}).describe("Due date in ISO 8601 format"),
            }),
          )
          .describe("Associated tasks (shown in detail view)"),
      }),
    )
    .describe("Array of users with their tasks"),
  meta: zod.object({
    total: zod
      .int()
      .min(listUsersResponseMetaTotalMin)
      .describe("Total count of items matching query ignoring pagination"),
    page: zod.int().min(1).describe("Current page number"),
    perPage: zod.int().min(1).describe("Number of items returned per page"),
  }),
});

/**
 * Create a new user with minimal required fields.
 * The user will be initialized with default values for:
 * - isActive: true
 * - bio: null
 * - onboardingProgress: 0
 * - taskCompletionRate: 0
 * - seniority: 1
 * - loginCount: 0
 * - lastLoginAt: current timestamp
 * - tasks: empty array
 * - image: deterministically generated based on user ID
 * - department: deterministically generated based on user ID
 * @summary Create a new user
 */
export const createUserBodyNameMax = 255;

export const CreateUserBody = zod.object({
  name: zod.string().min(1).max(createUserBodyNameMax).describe("User's full name"),
  email: zod.email().describe("User's email address"),
  role: zod.enum(["admin", "editor", "viewer"]).describe("User's role"),
  bio: zod.string().nullish().describe("User biography"),
  seniority: zod
    .union([zod.literal(1), zod.literal(2), zod.literal(3), zod.literal(4), zod.literal(5)])
    .optional()
    .describe("Seniority level (1=junior, 5=senior)"),
  department: zod.string().optional().describe("Department or team name"),
});

export const createUserResponseTaskCompletionRateMin = 0;
export const createUserResponseTaskCompletionRateMax = 100;

export const createUserResponseOnboardingProgressMin = 0;
export const createUserResponseOnboardingProgressMax = 100;

export const createUserResponseLoginCountMin = 0;

export const CreateUserResponse = zod.object({
  id: zod.string().describe("Unique user identifier"),
  name: zod.string().describe("User's full name"),
  email: zod.email().describe("User's email address"),
  role: zod.enum(["admin", "editor", "viewer"]).describe("User's role"),
  createdAt: zod.iso.datetime({offset: true}).describe("Account creation date in ISO 8601 format"),
  image: zod.string().describe("Avatar image URL or asset name"),
  isActive: zod.boolean().describe("Whether the user account is active"),
  bio: zod.string().nullish().describe("User biography"),
  taskCompletionRate: zod
    .int()
    .min(createUserResponseTaskCompletionRateMin)
    .max(createUserResponseTaskCompletionRateMax)
    .describe("Task completion rate (0-100)"),
  onboardingProgress: zod
    .int()
    .min(createUserResponseOnboardingProgressMin)
    .max(createUserResponseOnboardingProgressMax)
    .describe("Onboarding progress (0-100)"),
  seniority: zod
    .union([zod.literal(1), zod.literal(2), zod.literal(3), zod.literal(4), zod.literal(5)])
    .describe("Seniority level (1=junior, 5=senior)"),
  loginCount: zod
    .int()
    .min(createUserResponseLoginCountMin)
    .describe("Total number of successful logins"),
  lastLoginAt: zod.iso
    .datetime({offset: true})
    .nullish()
    .describe("Date of last login in ISO 8601 format"),
  department: zod.string().describe("Department or team name"),
  tasks: zod
    .array(
      zod.object({
        id: zod.string().describe("Unique task identifier"),
        title: zod.string().describe("Task title"),
        status: zod.enum(["pending", "in-progress", "completed"]).describe("Task status"),
        dueAt: zod.iso.datetime({offset: true}).describe("Due date in ISO 8601 format"),
      }),
    )
    .describe("Associated tasks (shown in detail view)"),
});

/**
 * Retrieve a user by ID including all their associated tasks.
 * Throws 404 if the user does not exist.
 * @summary Get a single user by ID
 */
export const getUserResponseTaskCompletionRateMin = 0;
export const getUserResponseTaskCompletionRateMax = 100;

export const getUserResponseOnboardingProgressMin = 0;
export const getUserResponseOnboardingProgressMax = 100;

export const getUserResponseLoginCountMin = 0;

export const GetUserResponse = zod.object({
  id: zod.string().describe("Unique user identifier"),
  name: zod.string().describe("User's full name"),
  email: zod.email().describe("User's email address"),
  role: zod.enum(["admin", "editor", "viewer"]).describe("User's role"),
  createdAt: zod.iso.datetime({offset: true}).describe("Account creation date in ISO 8601 format"),
  image: zod.string().describe("Avatar image URL or asset name"),
  isActive: zod.boolean().describe("Whether the user account is active"),
  bio: zod.string().nullish().describe("User biography"),
  taskCompletionRate: zod
    .int()
    .min(getUserResponseTaskCompletionRateMin)
    .max(getUserResponseTaskCompletionRateMax)
    .describe("Task completion rate (0-100)"),
  onboardingProgress: zod
    .int()
    .min(getUserResponseOnboardingProgressMin)
    .max(getUserResponseOnboardingProgressMax)
    .describe("Onboarding progress (0-100)"),
  seniority: zod
    .union([zod.literal(1), zod.literal(2), zod.literal(3), zod.literal(4), zod.literal(5)])
    .describe("Seniority level (1=junior, 5=senior)"),
  loginCount: zod
    .int()
    .min(getUserResponseLoginCountMin)
    .describe("Total number of successful logins"),
  lastLoginAt: zod.iso
    .datetime({offset: true})
    .nullish()
    .describe("Date of last login in ISO 8601 format"),
  department: zod.string().describe("Department or team name"),
  tasks: zod
    .array(
      zod.object({
        id: zod.string().describe("Unique task identifier"),
        title: zod.string().describe("Task title"),
        status: zod.enum(["pending", "in-progress", "completed"]).describe("Task status"),
        dueAt: zod.iso.datetime({offset: true}).describe("Due date in ISO 8601 format"),
      }),
    )
    .describe("Associated tasks (shown in detail view)"),
});

/**
 * Partially update a user's mutable fields.
 * Only the provided fields will be updated; unspecified fields remain unchanged.
 * Immutable fields (id, createdAt) cannot be updated.
 * @summary Update a user
 */
export const updateUserBodyNameMax = 255;

export const UpdateUserBody = zod.object({
  name: zod.string().min(1).max(updateUserBodyNameMax).optional().describe("User's full name"),
  email: zod.email().optional().describe("User's email address"),
  role: zod.enum(["admin", "editor", "viewer"]).optional().describe("User's role"),
  bio: zod.string().nullish().describe("User biography"),
  isActive: zod.boolean().optional().describe("Whether the user account is active"),
  seniority: zod
    .union([zod.literal(1), zod.literal(2), zod.literal(3), zod.literal(4), zod.literal(5)])
    .optional()
    .describe("Seniority level (1=junior, 5=senior)"),
  department: zod.string().optional().describe("Department or team name"),
});

export const updateUserResponseTaskCompletionRateMin = 0;
export const updateUserResponseTaskCompletionRateMax = 100;

export const updateUserResponseOnboardingProgressMin = 0;
export const updateUserResponseOnboardingProgressMax = 100;

export const updateUserResponseLoginCountMin = 0;

export const UpdateUserResponse = zod.object({
  id: zod.string().describe("Unique user identifier"),
  name: zod.string().describe("User's full name"),
  email: zod.email().describe("User's email address"),
  role: zod.enum(["admin", "editor", "viewer"]).describe("User's role"),
  createdAt: zod.iso.datetime({offset: true}).describe("Account creation date in ISO 8601 format"),
  image: zod.string().describe("Avatar image URL or asset name"),
  isActive: zod.boolean().describe("Whether the user account is active"),
  bio: zod.string().nullish().describe("User biography"),
  taskCompletionRate: zod
    .int()
    .min(updateUserResponseTaskCompletionRateMin)
    .max(updateUserResponseTaskCompletionRateMax)
    .describe("Task completion rate (0-100)"),
  onboardingProgress: zod
    .int()
    .min(updateUserResponseOnboardingProgressMin)
    .max(updateUserResponseOnboardingProgressMax)
    .describe("Onboarding progress (0-100)"),
  seniority: zod
    .union([zod.literal(1), zod.literal(2), zod.literal(3), zod.literal(4), zod.literal(5)])
    .describe("Seniority level (1=junior, 5=senior)"),
  loginCount: zod
    .int()
    .min(updateUserResponseLoginCountMin)
    .describe("Total number of successful logins"),
  lastLoginAt: zod.iso
    .datetime({offset: true})
    .nullish()
    .describe("Date of last login in ISO 8601 format"),
  department: zod.string().describe("Department or team name"),
  tasks: zod
    .array(
      zod.object({
        id: zod.string().describe("Unique task identifier"),
        title: zod.string().describe("Task title"),
        status: zod.enum(["pending", "in-progress", "completed"]).describe("Task status"),
        dueAt: zod.iso.datetime({offset: true}).describe("Due date in ISO 8601 format"),
      }),
    )
    .describe("Associated tasks (shown in detail view)"),
});

/**
 * Delete a user by ID. All associated tasks are also deleted.
 * Throws 404 if the user does not exist.
 * @summary Delete a user
 */
export const DeleteUserResponse = zod.void();
